<template>
    <div class="view-container">
        <div class="dashboard">
            <div class="dashboard__toolbar">
                <div class="dashboard__filters">
                    <button
                        v-for="chip in chips"
                        :key="chip.key"
                        type="button"
                        class="status-chip"
                        :class="['status-chip--' + chip.key, { 'status-chip--active': activeFilter === chip.key }]"
                        @click="setFilter(chip.key)"
                    >
                        <span class="status-chip__dot"></span>
                        <span class="status-chip__count">{{ chip.count }}</span>
                        <span class="status-chip__label">{{ $t(chip.label) }}</span>
                    </button>
                </div>
                <input
                    v-model="search"
                    type="text"
                    class="dashboard__search"
                    :placeholder="$t('SearchSystem')"
                />
            </div>

            <div v-if="isLoading" class="tiles">
                <DashboardTileSkeleton v-for="n in 12" :key="'skeleton-' + n" />
            </div>
            <div v-else-if="visibleSystems.length === 0" class="dashboard__empty">
                {{ $t('NoSystemsToShow') }}
            </div>
            <div v-else class="tiles">
                <DashboardTile v-for="item in visibleSystems" :key="item.id" :item="item" />
            </div>
        </div>
    </div>
</template>

<script>
import { getSystemStatusList } from "@api";
import DashboardTile from '../components/Dashboard/DashboardTile.vue'
import DashboardTileSkeleton from '../components/Dashboard/DashboardTileSkeleton.vue'

// Kolejnosc odpowiada wartosci pola status z API: 0 - ok, 1 - ostrzezenie, 2 - blad, 3 - system wylaczony.
// Kolory statusow sa w tokenach --status-* (assets/css/theme.css), osobno dla motywu jasnego i ciemnego.
const STATUS_META = [
    { key: 'ok', label: 'Working Fine' },
    { key: 'warning', label: 'Warning' },
    { key: 'error', label: 'Error' },
    { key: 'down', label: 'System Down' }
]

export default {
    props: {},
    components: {
        DashboardTile,
        DashboardTileSkeleton
    },
    data() {
        return {
            systems: [],
            interval: null,
            isLoading: true,
            activeFilter: 'all',
            search: ''
        }
    },
    computed: {
        chips() {
            let chips = [{ key: 'all', label: 'ALL_SYSTEMS', count: this.systems.length }]

            // Od najpowazniejszego statusu, zeby problemy byly pierwsze pod reka.
            for (let status = STATUS_META.length - 1; status >= 0; status--) {
                let meta = STATUS_META[status]
                chips.push({
                    key: meta.key,
                    label: meta.label,
                    count: this.systems.filter(x => x.status === status).length
                })
            }

            return chips
        },
        visibleSystems() {
            let phrase = this.search.trim().toLowerCase()

            return this.systems.filter(x => {
                if (this.activeFilter !== 'all' && STATUS_META[x.status].key !== this.activeFilter) {
                    return false
                }

                if (phrase !== '' && (x.name || '').toLowerCase().indexOf(phrase) === -1) {
                    return false
                }

                return true
            })
        }
    },
    methods: {
        setFilter(key) {
            this.activeFilter = key
        },
        async getData() {
            let result = await getSystemStatusList() || []

            result.forEach((s) => {
                if (s.statusDescription == "System down") {
                    s.status = 3
                }

                if (s.status == null || STATUS_META[s.status] == null) {
                    s.status = 0
                }

                s.statusDescription = s.statusDescription.split(',').map(x => this.$t(x.trim())).join(', ')
                s.lastUpdateFrom = new Date(s.lastUpdate).toAgoString()
            })

            this.systems = result.sort(this.doubleSorting)
            this.isLoading = false
        },
        doubleSorting(a, b) {
            if (a.status != b.status) {
                return (b.status - a.status);
            }
            else {
                return (a.name.localeCompare(b.name, undefined, {
                    numeric: true,
                    sensitivity: 'base'
                }));
            }
        },
        intervalMethod() {
            // Odswiezanie w tle - bez szkieletu i bez globalnego spinnera, zeby widok nie migotal.
            this.getData()
        }
    },
    mounted: async function () {
        await this.getData();
        this.interval = setInterval(() => this.intervalMethod(), 15000);
    },
    destroyed() {
        clearInterval(this.interval);
    },
}
</script>

<style scoped>
.dashboard {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.dashboard__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 15px;
}

.dashboard__filters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

/* Kolory z tokenów --status-* i --dash-* (assets/css/theme.css). */
.status-chip {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border: 1px solid var(--dash-border);
    border-radius: 6px;
    background: var(--surface);
    color: var(--dash-text);
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.status-chip--all { --chip-color: var(--dash-accent); }
.status-chip--ok { --chip-color: var(--status-ok); }
.status-chip--warning { --chip-color: var(--status-warning); }
.status-chip--error { --chip-color: var(--status-error); }
.status-chip--down { --chip-color: var(--status-down); }

.status-chip:hover {
    border-color: var(--dash-border-hover);
}

.status-chip--active,
.status-chip--active:hover {
    border-color: var(--chip-color);
    box-shadow: inset 0 -3px 0 0 var(--chip-color);
    color: var(--dash-text-strong);
}

.status-chip__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: var(--chip-color);
}

.status-chip__count {
    font-size: 16px;
    font-weight: bold;
}

.status-chip__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: var(--dash-muted);
}

.status-chip--active .status-chip__label {
    color: var(--dash-text);
}

.dashboard__search {
    width: 220px;
    padding: 7px 12px;
    border: 1px solid var(--dash-border);
    border-radius: 6px;
    background: var(--surface);
    font-size: 13px;
    color: var(--dash-text);
}

.dashboard__search:focus {
    border-color: var(--dash-accent);
    outline: none;
}

.dashboard__empty {
    padding: 40px 0;
    text-align: center;
    font-size: 14px;
    color: var(--dash-muted);
}

.tiles {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 15px 20px;
    width: 100%;
    align-content: start;
}
</style>



// WEBPACK FOOTER //
// src/views/Dashboard.vue